// Fixture mirroring the TVL /api/v1/impact response shape. Hand-written
// slice across 3 regions, 3 industries (two NAICS hits + one BEA-only
// aggregate with naics: null), all 3 dispositions, a subset of tax
// categories/subcategories, and the full decile range — enough to
// exercise every branch a chart/table consumer might hit without
// returning the live endpoint's full ~1000-row payload.

const US = '00000';
const NY = '36000';
const CA = '06000';

const DISPOSITIONS = ['direct', 'indirect', 'induced'];
const LEVELS = ['federal', 'state', 'local'];

const TAX_CATEGORIES = [
  { category: 'topi', subcategories: ['sales tax', 'property', 'excise'] },
  {
    category: 'employee compensation',
    subcategories: ['medicare', 'social security'],
  },
  { category: 'personal income', subcategories: [''] },
  { category: 'corporate profits', subcategories: [''] },
];

const INDUSTRIES = [
  {
    bea: '11',
    naics: '11',
    display_description: 'Agriculture, forestry, fishing, and hunting',
  },
  { bea: '21', naics: '21', display_description: 'Mining' },
  {
    bea: 'FIRE',
    naics: null,
    display_description: 'Finance, insurance, real estate, rental, and leasing',
  },
];

const REGIONS = [
  { fips: US, name: 'United States' },
  { fips: NY, name: 'New York' },
  { fips: CA, name: 'California' },
];

// Deterministic-but-simple numeric stub: hash region/category/... into
// a [0,1) float, then scale to the declared bound. Mirrors the shape
// of the backend's md5-seeded per-index-tuple stub without importing a
// crypto dep into the browser fixture.
const stub = (scale, ...parts) => {
  const key = parts.join('|');
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const unit = ((h >>> 0) % 1_000_000) / 1_000_000;
  return unit * scale;
};

const taxes = REGIONS.flatMap(({ fips }) =>
  TAX_CATEGORIES.flatMap(({ category, subcategories }) =>
    subcategories.flatMap((subcategory) =>
      LEVELS.flatMap((level) =>
        DISPOSITIONS.map((disposition) => ({
          region: { fips },
          category,
          subcategory,
          level,
          value: stub(
            1_000_000,
            'tax',
            fips,
            category,
            subcategory,
            level,
            disposition
          ),
          disposition,
        }))
      )
    )
  )
);

const industry = REGIONS.flatMap(({ fips }) =>
  INDUSTRIES.flatMap(({ bea, naics, display_description }) =>
    DISPOSITIONS.map((disposition) => ({
      region: { fips },
      industry: { naics, bea },
      display_description,
      total_output: stub(10_000_000, 'industry_output', fips, bea, disposition),
      impact_delta:
        stub(1_000_000, 'industry_delta', fips, bea, disposition) - 500_000,
      disposition,
      household_spending: [
        {
          region: { fips },
          value: stub(100_000, 'industry_hh', fips, bea, disposition, fips),
        },
      ],
    }))
  )
);

const DECILES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const household = REGIONS.flatMap(({ fips, name }) =>
  DECILES.flatMap((decile) =>
    DISPOSITIONS.map((disposition) => ({
      region: { fips },
      income_decile: decile,
      display_description: `${name} — income decile ${decile}`,
      income_deltas: {
        labor: stub(100_000, 'hh_labor', fips, decile, disposition) - 50_000,
      },
      total_output: stub(500_000, 'hh_output', fips, decile, disposition),
      impact_delta:
        stub(50_000, 'hh_delta', fips, decile, disposition) - 25_000,
      disposition,
    }))
  )
);

export const impactResponse = {
  impact: {
    taxes,
    mrio: { industry, household },
  },
};

// Server-side filters, mirrored for fidelity with the real endpoint.
// The real view applies these against sibling DB reads; here we apply
// them to the already-built payload so toggling MSW off against the
// real backend returns a similarly-shaped subset.
export function filterImpactResponse({ fips, region, bea, naics } = {}) {
  const wantFips = fips || null;
  const wantRegionName = region ? region.toLowerCase() : null;
  const regionByFips = new Map(REGIONS.map((r) => [r.fips, r]));
  const matchesRegion = (rowFips) => {
    if (wantFips && rowFips !== wantFips) return false;
    if (wantRegionName) {
      const row = regionByFips.get(rowFips);
      if (!row || row.name.toLowerCase() !== wantRegionName) return false;
    }
    return true;
  };

  const matchesIndustry = (row) => {
    if (bea && row.industry.bea !== bea) return false;
    if (naics) {
      if (row.industry.naics === null) return false;
      if (row.industry.naics !== naics) return false;
    }
    return true;
  };

  return {
    impact: {
      taxes: impactResponse.impact.taxes.filter((r) =>
        matchesRegion(r.region.fips)
      ),
      mrio: {
        industry: impactResponse.impact.mrio.industry.filter(
          (r) => matchesRegion(r.region.fips) && matchesIndustry(r)
        ),
        household: impactResponse.impact.mrio.household.filter((r) =>
          matchesRegion(r.region.fips)
        ),
      },
    },
  };
}
